import type { CueScript } from "../types";

export interface SearchPreview {
  before: string;
  match: string;
  after: string;
}
export interface LibrarySearchResult {
  script: CueScript;
  preview?: SearchPreview;
}

const whitespace = (text: string) => text.replace(/\s+/gu, " ").trim();

/** Read visible script text from inert markup, without indexing styles or attributes. */
export function searchableScriptText(html: string): string {
  const template = document.createElement("template");
  template.innerHTML = html;
  template.content.querySelectorAll("script,style,template,noscript").forEach((el) => el.remove());
  for (const el of template.content.querySelectorAll("p,div,h1,h2,h3,blockquote,li,br,hr")) {
    el.before(" ");
    el.after(" ");
  }
  return whitespace(template.content.textContent || "");
}

function preview(text: string, start: number, length: number): SearchPreview {
  const end = start + length;
  let from = Math.max(0, start - 38);
  let to = Math.min(text.length, end + 60);
  // Keep the surrounding words intact while bounding the preview in a narrow library.
  if (from) {
    const boundary = text.indexOf(" ", from);
    if (boundary < start && boundary >= 0) from = boundary + 1;
  }
  if (to < text.length) {
    const boundary = text.lastIndexOf(" ", to);
    if (boundary > end) to = boundary;
  }
  return {
    before: (from ? "…" : "") + text.slice(from, start),
    match: text.slice(start, Math.min(end, start + 80)) + (length > 80 ? "…" : ""),
    after: text.slice(end, to) + (to < text.length ? "…" : ""),
  };
}

function literalRange(text: string, folded: string, term: string): { start: number; length: number } | undefined {
  const start = folded.indexOf(term);
  if (start < 0) return;
  if (folded.length === text.length) return { start, length: term.length };
  // Some Unicode letters expand when lowercased. Keep the excerpt tied to original characters.
  let sourceOffset = 0;
  let foldedOffset = 0;
  let sourceStart = 0;
  for (const char of text) {
    const next = foldedOffset + char.toLowerCase().length;
    if (foldedOffset <= start && start < next) sourceStart = sourceOffset;
    if (foldedOffset < start + term.length && start + term.length <= next) {
      return { start: sourceStart, length: sourceOffset + char.length - sourceStart };
    }
    sourceOffset += char.length;
    foldedOffset = next;
  }
}

/** Keep text extraction off the playback path and reuse unchanged scripts across queries. */
export class LibrarySearch {
  private textCache = new Map<string, { html: string; text: string; folded: string }>();

  search(scripts: CueScript[], query: string): LibrarySearchResult[] {
    const ids = new Set(scripts.map((script) => script.id));
    for (const id of this.textCache.keys()) if (!ids.has(id)) this.textCache.delete(id);
    const term = whitespace(query).toLowerCase();
    if (!term) return scripts.map((script) => ({ script }));
    const results: LibrarySearchResult[] = [];
    for (const script of scripts) {
      let cached = this.textCache.get(script.id);
      if (!cached || cached.html !== script.html) {
        const text = searchableScriptText(script.html);
        cached = { html: script.html, text, folded: text.toLowerCase() };
        this.textCache.set(script.id, cached);
      }
      const match = literalRange(cached.text, cached.folded, term);
      if (match || whitespace(`${script.title} ${script.tags.join(" ")}`).toLowerCase().includes(term)) {
        results.push({
          script,
          preview: match ? preview(cached.text, match.start, match.length) : undefined,
        });
      }
    }
    return results;
  }
}
